type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
};

const buildPages = (current: number, total: number): number[] => {
  if (total <= 6) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);
};

export default function Pagination({ currentPage, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;
  const pages = buildPages(currentPage, totalPages);

  return (
    <div className="flex items-center justify-center gap-1.5 flex-wrap">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        aria-label="上一页"
        className="flex items-center justify-center w-9 h-9 rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-700 transition-colors cursor-pointer disabled:cursor-not-allowed"
      >
        <i className="ri-arrow-left-s-line text-base"></i>
      </button>
      {pages.map((page, index) => {
        const prev = pages[index - 1];
        return (
          <span key={page} className="flex items-center gap-1.5">
            {prev && page - prev > 1 && <span className="text-foreground-400 px-1">…</span>}
            <button
              type="button"
              onClick={() => onChange(page)}
              aria-current={page === currentPage ? "page" : undefined}
              className={`min-w-9 h-9 px-2 rounded-md text-sm transition-colors cursor-pointer ${
                page === currentPage
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              {page}
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        aria-label="下一页"
        className="flex items-center justify-center w-9 h-9 rounded-md border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-700 transition-colors cursor-pointer disabled:cursor-not-allowed"
      >
        <i className="ri-arrow-right-s-line text-base"></i>
      </button>
    </div>
  );
}